import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, DownloadSimple, CircleNotch, ArrowsClockwise, UploadSimple, ScanSmiley, User, Buildings, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

import BulkTestRecordsPanel from "./BulkTestRecordsPanel";

import { API } from "@/config/api";

const toMMDDYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const toISO = (mmddyyyy) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((mmddyyyy || "").trim());
  if (!match) return "";
  const [, mo, da, yr] = match;
  return `${yr}-${mo.padStart(2, "0")}-${da.padStart(2, "0")}`;
};
const normaliseGender = (g) => {
  if (!g) return "";
  const v = String(g).toLowerCase();
  if (v.startsWith("m")) return "Male";
  if (v.startsWith("f")) return "Female";
  return "";
};

export default function ScoreReportGenerator() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", gender: "", institution: "focus", intake_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [savingToStudent, setSavingToStudent] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [result, setResult] = useState(null);
  const objectUrlRef = useRef(null);

  // Recruiter → Student picker (NEW)
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState("");
  const [recruiterStudents, setRecruiterStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);
  // History list for the selected student
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Bulk-delete selection (report ids) within the history list
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ----- Custom fields mode -----
  // Admin opens an advanced panel and types the EXACT value for every randomised
  // field. Any field left blank falls back to the auto-generated value.
  const [customMode, setCustomMode] = useState(false);
  const [customGenerating, setCustomGenerating] = useState(false);
  const [custom, setCustom] = useState({
    exnum: "", student_id: "",
    reading_score: "", writing_score: "", writeplacer_score: "",
    test_date: "", session_started: "", session_ended: "", total_time: "", print_ts: "",
  });
  const setCustomField = (key) => (e) => setCustom((c) => ({ ...c, [key]: e.target.value }));

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  // Initial recruiter list (one-time)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/recruiters`);
        setRecruiters(data || []);
      } catch {
        // silent — picker just shows empty
      }
    })();
  }, []);

  // Load that recruiter's students whenever recruiter changes
  useEffect(() => {
    if (!selectedRecruiterId) {
      setRecruiterStudents([]); setSelectedStudentId(""); return;
    }
    setLoadingStudents(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/recruiters/${selectedRecruiterId}/students`);
        setRecruiterStudents(data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load this recruiter's students");
        setRecruiterStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, [selectedRecruiterId]);

  const selectedStudent = useMemo(
    () => recruiterStudents.find((s) => s.id === selectedStudentId) || null,
    [recruiterStudents, selectedStudentId]
  );

  // Auto-fill manual form whenever the picked student changes
  useEffect(() => {
    if (!selectedStudent) return;
    const parts = (selectedStudent.full_name || "").trim().split(/\s+/);
    setForm((f) => ({
      ...f,
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" ") || "",
      dob: selectedStudent.dob || "",
      gender: normaliseGender(selectedStudent.gender),
    }));
  }, [selectedStudent]);

  // Load saved reports whenever the selected student changes
  useEffect(() => {
    if (!selectedStudentId) { setHistory([]); setSelectedReportIds([]); return; }
    setLoadingHistory(true);
    setSelectedReportIds([]);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/students/${selectedStudentId}/score-reports`);
        setHistory(data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load previous reports");
        setHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [selectedStudentId]);

  const refreshHistory = async () => {
    if (!selectedStudentId) return;
    try {
      const { data } = await axios.get(`${API}/admin/students/${selectedStudentId}/score-reports`);
      setHistory(data || []);
      // Drop any ticked ids that no longer exist in the refreshed list
      setSelectedReportIds((prev) => prev.filter((id) => (data || []).some((h) => h.id === id)));
    } catch { /* ignore */ }
  };

  const toggleReportSelected = (id) => {
    setSelectedReportIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAllReports = () => {
    if (selectedReportIds.length === history.length) setSelectedReportIds([]);
    else setSelectedReportIds(history.map((h) => h.id));
  };
  const handleBulkDeleteReports = async () => {
    if (!selectedReportIds.length) return;
    if (!window.confirm(`Permanently delete ${selectedReportIds.length} score report(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/score-reports/bulk-delete`, {
        report_ids: selectedReportIds,
      });
      toast.success(`${data.deleted} score report(s) deleted`);
      setSelectedReportIds([]);
      refreshHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const fetchPdfBlobUrl = async (data, seed, dobStr) => {
    const params = new URLSearchParams({
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      dob: dobStr,
      gender: data.gender,
      institution: data.institution,
      seed: String(seed),
    });
    if (data.intake_date) params.set('intake_date', data.intake_date);
    const res = await axios.get(`${API}/document?${params.toString()}`, { responseType: "blob" });
    // Create TWO blob URLs from the same bytes: one lives for the iframe
    // preview, one is handed to the download link. Reusing a single URL
    // for both causes Chrome to invalidate the iframe when `<a download>`
    // is clicked, leaving the preview blank.
    const previewBlob = new Blob([res.data], { type: "application/pdf" });
    const downloadBlob = new Blob([res.data], { type: "application/pdf" });
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(previewBlob);
    objectUrlRef.current = url;
    const downloadUrl = URL.createObjectURL(downloadBlob);
    return { url, downloadUrl };
  };

  const triggerBlobDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const validateForm = (data) => {
    if (!data.first_name.trim() || !data.last_name.trim()) { toast.error("Enter the student's first and last name."); return false; }
    if (!data.dob) { toast.error("Select date of birth."); return false; }
    if (!data.gender) { toast.error("Select a gender."); return false; }
    return true;
  };

  // Anonymous flow (legacy / quick-fill / manual without picking a student)
  const doGenerate = async (data) => {
    if (!validateForm(data)) return;
    setLoading(true);
    try {
      const dobStr = toMMDDYYYY(data.dob);
      const { data: res } = await axios.post(`${API}/prepare`, {
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        dob: dobStr,
        gender: data.gender,
        institution: data.institution,
        intake_date: data.intake_date || null,
      });
      const { url, downloadUrl } = await fetchPdfBlobUrl(data, res.seed, dobStr);
      setResult({ url, downloadUrl, filename: res.filename, fields: res.fields, seed: res.seed, dobStr });
      toast.success("Report generated — preview below.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to generate the report. Please try again.");
    } finally { setLoading(false); }
  };

  // Custom-fields flow: admin types the exact value for every randomised
  // field. Hits POST /api/score-report/custom-document which streams the PDF.
  const doGenerateCustom = async () => {
    if (!validateForm(form)) return;
    setCustomGenerating(true);
    try {
      const dobStr = toMMDDYYYY(form.dob);
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        dob: dobStr,
        gender: form.gender,
        intake_date: form.intake_date || null,
      };
      // Only forward overrides the admin actually filled in.
      const trim = (v) => (typeof v === "string" ? v.trim() : v);
      if (trim(custom.exnum))             payload.exnum             = trim(custom.exnum);
      if (trim(custom.student_id))        payload.student_id        = trim(custom.student_id);
      if (trim(custom.reading_score))     payload.reading_score     = parseInt(custom.reading_score, 10);
      if (trim(custom.writing_score))     payload.writing_score     = parseInt(custom.writing_score, 10);
      if (trim(custom.writeplacer_score)) payload.writeplacer_score = parseInt(custom.writeplacer_score, 10);
      if (trim(custom.test_date))         payload.test_date         = trim(custom.test_date);
      if (trim(custom.session_started))   payload.session_started   = trim(custom.session_started);
      if (trim(custom.session_ended))     payload.session_ended     = trim(custom.session_ended);
      if (trim(custom.total_time))        payload.total_time        = trim(custom.total_time);
      if (trim(custom.print_ts))          payload.print_ts          = trim(custom.print_ts);
      const res = await axios.post(`${API}/score-report/custom-document`, payload, { responseType: "blob" });
      // Create TWO blob URLs from the same bytes: one for the <iframe> preview
      // (long-lived until next generate/reset), and a SEPARATE one for the
      // download link so the browser can revoke the download URL on its own
      // without wiping out the iframe. Reusing one URL across both breaks the
      // preview in Chrome: triggering `<a download>` on the shared URL leaves
      // the iframe pointing at a now-invalidated blob and it renders blank.
      const previewBlob = new Blob([res.data], { type: "application/pdf" });
      const downloadBlob = new Blob([res.data], { type: "application/pdf" });
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(previewBlob);
      objectUrlRef.current = url;
      const downloadUrl = URL.createObjectURL(downloadBlob);
      // Try to recover the server-chosen filename + exnum from headers.
      const exnum = res.headers["x-report-exnum"] || payload.exnum || "custom";
      let fname = `Exnum ${exnum} Branching Profile Name 01. Reading, Writing, WritePlacer.pdf`;
      try {
        const h = res.headers["x-report-filename"];
        if (h) fname = decodeURIComponent(h);
      } catch { /* fall back */ }
      setResult({
        url,
        downloadUrl,
        filename: fname,
        fields: {
          exnum,
          reading_score: payload.reading_score ?? "—",
          writing_score: payload.writing_score ?? "—",
          writeplacer_score: payload.writeplacer_score ?? "—",
        },
        seed: null,
        dobStr,
      });
      toast.success("Custom report generated — preview below.");
    } catch (err) {
      console.error(err);
      // responseType is 'blob' so error body is a Blob; try to decode it.
      let msg = "Failed to generate the custom report.";
      try {
        if (err?.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed?.detail || msg;
        } else {
          msg = err?.response?.data?.detail || msg;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally { setCustomGenerating(false); }
  };

  // Persisted flow: generate AND attach to the chosen student's profile
  const handleGenerateAndSave = async () => {
    if (!selectedStudentId) { toast.error("Pick a recruiter and student first."); return; }
    if (!validateForm(form)) return;
    setSavingToStudent(true);
    try {
      const dobStr = toMMDDYYYY(form.dob);
      const { data: rec } = await axios.post(`${API}/admin/students/${selectedStudentId}/score-reports`, {
        institution: form.institution,
        // Send overrides so the backend picks up any in-form edits
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        dob: dobStr,
        gender: form.gender,
        intake_date: form.intake_date || null,
      });
      // Pull the PDF for inline preview / download
      const fileRes = await axios.get(`${API}/admin/score-reports/${rec.id}/file`, { responseType: "blob" });
      // Separate blob URLs for preview vs download — see fetchPdfBlobUrl for why.
      const previewBlob = new Blob([fileRes.data], { type: "application/pdf" });
      const downloadBlob = new Blob([fileRes.data], { type: "application/pdf" });
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(previewBlob);
      objectUrlRef.current = url;
      const downloadUrl = URL.createObjectURL(downloadBlob);
      setResult({
        url,
        downloadUrl,
        filename: rec.filename,
        fields: { exnum: rec.scores.exnum, reading_score: rec.scores.reading, writing_score: rec.scores.writing, writeplacer_score: rec.scores.writeplacer },
        seed: rec.seed,
        dobStr,
        savedRecordId: rec.id,
      });
      toast.success(`Saved to ${rec.student_name}'s profile — preview below.`);
      refreshHistory();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to generate & save the report.");
    } finally { setSavingToStudent(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadName(file.name);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/extract-info`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const dobIso = data.dob ? toISO(data.dob) : "";
      const filled = { ...form, first_name: data.first_name || "", last_name: data.last_name || "", dob: dobIso, gender: data.gender || "" };
      setForm(filled);
      const missing = [];
      if (!filled.first_name) missing.push("first name");
      if (!filled.last_name) missing.push("last name");
      if (!filled.dob) missing.push("date of birth");
      if (!filled.gender) missing.push("gender");
      if (missing.length) {
        toast.warning(`Couldn't read: ${missing.join(", ")}. Please complete it manually.`);
      } else {
        toast.success("Details extracted — generating report…");
        await doGenerate(filled);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Couldn't read that document.");
    } finally { setExtracting(false); }
  };

  const handleDownload = () => {
    if (!result) return;
    // Lazily mint a fresh download blob URL on each click so a previous
    // download can't invalidate the preview. result.downloadUrl is the
    // pre-created one from generation; result.url is the long-lived preview.
    const url = result.downloadUrl || result.url;
    triggerBlobDownload(url, result.filename);
  };
  const handleOpenInNewTab = () => { if (result) window.open(result.url, "_blank", "noopener,noreferrer"); };
  const handleReset = () => {
    setForm({ first_name: "", last_name: "", dob: "", gender: "", institution: "focus", intake_date: "" });
    setUploadName(""); setResult(null);
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
  };

  const handleDownloadHistory = async (rec) => {
    try {
      const res = await axios.get(`${API}/admin/score-reports/${rec.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = rec.filename || "score-report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download");
    }
  };

  const handleDeleteHistory = async (rec) => {
    if (!window.confirm(`Delete score report from ${new Date(rec.generated_at).toLocaleDateString()}? Storage file is kept; only the record is removed.`)) return;
    try {
      await axios.delete(`${API}/admin/score-reports/${rec.id}`);
      toast.success("Deleted");
      refreshHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const isStudentLinked = !!selectedStudent;

  return (
    <div className="space-y-6" data-testid="score-report-root">
      {/* Bulk Test Records from Excel (admin-only). Isolated feature — never
          mixes with the student-linked score-report flow below. */}
      <BulkTestRecordsPanel />

      {/* Pick from existing students */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="picker-card">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Pick from existing students</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5"><Buildings size={12} /> Recruiter</Label>
            <Select value={selectedRecruiterId} onValueChange={(v) => setSelectedRecruiterId(v)}>
              <SelectTrigger data-testid="picker-recruiter-select"><SelectValue placeholder={recruiters.length ? "Pick a recruiter…" : "Loading recruiters…"} /></SelectTrigger>
              <SelectContent>
                {recruiters.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`picker-recruiter-option-${r.id}`}>
                    {r.full_name} <span className="text-gray-400 text-xs ml-1">· {r.company_name || r.email}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5"><User size={12} /> Student</Label>
            <Select
              value={selectedStudentId}
              onValueChange={(v) => setSelectedStudentId(v)}
              disabled={!selectedRecruiterId || loadingStudents}
            >
              <SelectTrigger data-testid="picker-student-select">
                <SelectValue placeholder={!selectedRecruiterId ? "Pick a recruiter first" : loadingStudents ? "Loading students…" : recruiterStudents.length === 0 ? "No students under this recruiter" : "Pick a student…"} />
              </SelectTrigger>
              <SelectContent>
                {recruiterStudents.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`picker-student-option-${s.id}`}>
                    {s.full_name}
                    {s.email ? <span className="text-gray-400 text-xs ml-1">· {s.email}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {selectedStudent && (
          <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="picker-autofill-banner">
            ✓ Auto-filled from <strong>{selectedStudent.full_name}</strong>{selectedStudent.dob ? ` · DOB ${selectedStudent.dob}` : ""}{selectedStudent.gender ? ` · ${normaliseGender(selectedStudent.gender) || selectedStudent.gender}` : ""}. Edit any field below if needed before generating.
          </div>
        )}
      </div>

      {/* Quick fill — upload tile (kept) */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Or quick-fill from an ID</p>
        <label htmlFor="sr-doc-upload" data-testid="upload-dropzone"
          className="group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40">
          <input id="sr-doc-upload" data-testid="upload-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={handleUpload} disabled={extracting || loading} />
          {extracting ? <ScanSmiley size={26} className="text-blue-600 animate-pulse" weight="duotone" /> : <UploadSimple size={26} className="text-gray-400 transition-transform group-hover:-translate-y-0.5" />}
          <div>
            <p className="text-sm font-medium text-gray-900">{extracting ? "Reading document…" : "Upload passport, ID or profile"}</p>
            <p className="mt-0.5 text-xs text-gray-500">{extracting ? "Extracting name, date of birth & gender" : "Auto-fills name, DOB & gender from JPG / PNG / PDF"}</p>
            {uploadName && !extracting && (<p className="mt-1.5 text-xs text-blue-700" data-testid="upload-filename">{uploadName}</p>)}
          </div>
        </label>
      </div>

      {/* Manual entry / edit-after-pick */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{isStudentLinked ? "Review & edit (auto-filled)" : "Or enter manually"}</p>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer" data-testid="custom-mode-toggle-label">
              <Checkbox checked={customMode} onCheckedChange={(v) => setCustomMode(!!v)} data-testid="custom-mode-toggle" />
              Fill every field manually (custom)
            </label>
            <p className="text-[11px] text-gray-400">Step 01 · Student Details</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label htmlFor="sr-first-name" className="text-xs text-gray-600">First Name</Label><Input id="sr-first-name" data-testid="input-first-name" placeholder="Jagjeet" value={form.first_name} onChange={setField("first_name")} /></div>
          <div className="space-y-1.5"><Label htmlFor="sr-last-name" className="text-xs text-gray-600">Last Name</Label><Input id="sr-last-name" data-testid="input-last-name" placeholder="Singh" value={form.last_name} onChange={setField("last_name")} /></div>
          <div className="space-y-1.5"><Label htmlFor="sr-dob" className="text-xs text-gray-600">Date of Birth</Label><Input id="sr-dob" data-testid="input-dob" type="date" value={form.dob} onChange={setField("dob")} /></div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Gender</Label>
            <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger data-testid="select-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
              <SelectContent><SelectItem value="Male" data-testid="gender-male">Male</SelectItem><SelectItem value="Female" data-testid="gender-female">Female</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-gray-600">Institution</Label>
            <Select value={form.institution} onValueChange={(v) => setForm((f) => ({ ...f, institution: v }))}>
              <SelectTrigger data-testid="select-institution"><SelectValue placeholder="Select institution" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="focus" data-testid="institution-focus">Focus College — Abbotsford Campus</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500">Every Accuplacer score report is issued under Focus College.</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-gray-600">Intake Date <span className="text-gray-400">(optional)</span></Label>
            <Input
              type="date"
              value={form.intake_date || ''}
              onChange={(e) => setForm((f) => ({ ...f, intake_date: e.target.value }))}
              data-testid="score-report-intake-date"
            />
            <p className="text-[11px] text-gray-500">
              When set, every randomised date on the report (test date, session start/end, print time) will fall strictly BEFORE this intake date.
            </p>
          </div>
        </div>

        {/* Custom-fields panel — admin fills every randomised field by hand */}
        {customMode && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="custom-fields-panel">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Custom values</p>
                <p className="text-[11px] text-amber-700 mt-0.5">Any field left blank will fall back to the auto-generated value. The report always uses Focus College.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-gray-700">Exnum (8 digits)</Label><Input value={custom.exnum} onChange={setCustomField("exnum")} placeholder="e.g. 77777777" data-testid="custom-exnum" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Student ID (8 digits)</Label><Input value={custom.student_id} onChange={setCustomField("student_id")} placeholder="e.g. 12345678" data-testid="custom-student-id" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Reading score (200-300)</Label><Input type="number" min="200" max="300" value={custom.reading_score} onChange={setCustomField("reading_score")} placeholder="e.g. 265" data-testid="custom-reading" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Writing score (200-300)</Label><Input type="number" min="200" max="300" value={custom.writing_score} onChange={setCustomField("writing_score")} placeholder="e.g. 252" data-testid="custom-writing" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">WritePlacer score (1-8)</Label><Input type="number" min="1" max="8" value={custom.writeplacer_score} onChange={setCustomField("writeplacer_score")} placeholder="e.g. 5" data-testid="custom-writeplacer" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Test date (MM/DD/YYYY)</Label><Input value={custom.test_date} onChange={setCustomField("test_date")} placeholder="06/18/2026" data-testid="custom-test-date" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs text-gray-700">Session started</Label><Input value={custom.session_started} onChange={setCustomField("session_started")} placeholder="06/18/2026 08:14 AM PDT" data-testid="custom-session-started" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs text-gray-700">Session ended</Label><Input value={custom.session_ended} onChange={setCustomField("session_ended")} placeholder="06/18/2026 10:45 AM PDT" data-testid="custom-session-ended" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Total time</Label><Input value={custom.total_time} onChange={setCustomField("total_time")} placeholder="02h 31min:00s" data-testid="custom-total-time" /></div>
              <div className="space-y-1"><Label className="text-xs text-gray-700">Printed timestamp</Label><Input value={custom.print_ts} onChange={setCustomField("print_ts")} placeholder="6/18/26, 11:05 AM" data-testid="custom-print-ts" /></div>
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          {customMode ? (
            <Button onClick={doGenerateCustom} disabled={customGenerating} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="generate-custom-report-btn">
              {customGenerating ? (<><CircleNotch size={14} className="mr-1.5 animate-spin" /> Generating…</>) : (<><FileText size={14} className="mr-1.5" /> Generate Custom Report</>)}
            </Button>
          ) : isStudentLinked ? (
            <Button onClick={handleGenerateAndSave} disabled={savingToStudent} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="generate-and-save-btn">
              {savingToStudent ? (<><CircleNotch size={14} className="mr-1.5 animate-spin" /> Generating & Saving…</>) : (<><FileText size={14} className="mr-1.5" /> Generate &amp; Save to Student&apos;s Profile</>)}
            </Button>
          ) : (
            <Button onClick={() => doGenerate(form)} disabled={loading} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="generate-report-btn">
              {loading ? (<><CircleNotch size={14} className="mr-1.5 animate-spin" /> Generating…</>) : (<><FileText size={14} className="mr-1.5" /> Generate Report</>)}
            </Button>
          )}
          {result && (<Button variant="outline" onClick={handleReset} data-testid="reset-btn"><ArrowsClockwise size={14} className="mr-1.5" /> New Report</Button>)}
        </div>
      </div>

      {/* Result card */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="result-section">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Preview</p>
            <p className="text-[11px] text-gray-400">Step 02 · Generated Report</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[["Exnum", result.fields.exnum], ["Reading", result.fields.reading_score], ["Writing", result.fields.writing_score], ["WritePlacer", result.fields.writeplacer_score]].map(([k, v]) => (
              <div key={k} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
                <div className="text-base font-semibold text-gray-900 font-mono" data-testid={`field-${k.toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-xs text-gray-500 font-mono truncate" data-testid="result-filename" title={result.filename}>{result.filename}</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleOpenInNewTab} data-testid="open-tab-btn">Open in new tab</Button>
              <Button size="sm" onClick={handleDownload} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="download-btn">
                <DownloadSimple size={14} className="mr-1.5" /> Download PDF
              </Button>
            </div>
          </div>
          <div className="rounded-md overflow-hidden border border-gray-200 bg-gray-100 relative">
            {/* <object> renders PDFs more reliably than <iframe> across Chrome
                versions when the src is a blob URL. If the browser can't
                render inline (e.g. mobile, no PDF plugin), the children
                show as a fallback with a direct Open link. */}
            <object data={result.url} type="application/pdf" className="h-[760px] w-full bg-white block" data-testid="pdf-preview">
              <div className="h-[760px] w-full flex flex-col items-center justify-center gap-3 text-sm text-gray-600 p-6 text-center">
                <FileText size={28} className="text-gray-400" />
                <p>Inline preview isn't supported in this browser.</p>
                <Button size="sm" onClick={handleOpenInNewTab}>Open the PDF in a new tab</Button>
              </div>
            </object>
          </div>
        </div>
      )}

      {/* Previous reports for the selected student */}
      {selectedStudent && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="history-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Previous Reports for {selectedStudent.full_name}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{history.length} saved · most recent first</p>
            </div>
            <Button size="sm" variant="ghost" onClick={refreshHistory} disabled={loadingHistory} data-testid="history-refresh-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
          </div>
          {loadingHistory ? (
            <p className="text-center text-xs text-gray-400 py-6">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-6" data-testid="history-empty">No reports saved for this student yet.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 mb-2 px-1" data-testid="history-bulk-toolbar">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                  <Checkbox
                    checked={selectedReportIds.length > 0 && selectedReportIds.length === history.length}
                    onCheckedChange={toggleAllReports}
                    data-testid="history-select-all"
                  />
                  {selectedReportIds.length ? `${selectedReportIds.length} selected` : "Select all"}
                </label>
                {selectedReportIds.length > 0 && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleBulkDeleteReports}
                    disabled={bulkDeleting}
                    data-testid="history-bulk-delete-btn"
                    className="h-7 px-2 text-xs"
                  >
                    {bulkDeleting ? <CircleNotch size={12} className="mr-1 animate-spin" /> : <Trash size={12} className="mr-1" />}
                    Delete Selected ({selectedReportIds.length})
                  </Button>
                )}
              </div>
              <div className="space-y-2" data-testid="history-list">
                {history.map((h, idx) => {
                  const v = history.length - idx;
                  const checked = selectedReportIds.includes(h.id);
                  return (
                    <div key={h.id} className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${checked ? "border-red-300 bg-red-50/60" : "border-gray-200 bg-white"}`} data-testid={`history-row-${h.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleReportSelected(h.id)}
                          data-testid={`history-select-${h.id}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-semibold text-gray-500">v{v}</span>
                            <span className="text-[11px] uppercase tracking-wide text-gray-500">{h.institution === "itd" ? "ITD" : "Focus"}</span>
                            <span className="text-[11px] text-gray-400">· {new Date(h.generated_at).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-gray-700 mt-0.5 font-mono">
                            Reading {h.scores?.reading} · Writing {h.scores?.writing} · WritePlacer {h.scores?.writeplacer}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">by {h.generated_by_admin}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadHistory(h)} className="h-7 px-2 text-xs" data-testid={`history-download-${h.id}`}>
                          <DownloadSimple size={12} className="mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteHistory(h)} className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" data-testid={`history-delete-${h.id}`}>
                          <Trash size={12} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
